import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';

// Services API
import employeService from '../../../services/employeService';

// Helper functions
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 HTG';
  return new Intl.NumberFormat('fr-HT', {
    style: 'currency',
    currency: 'HTG',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const calculateAnciennete = (dateEmbauche) => {
  if (!dateEmbauche) return '0 an(s)';
  
  const embauche = new Date(dateEmbauche);
  const now = new Date();
  
  let years = now.getFullYear() - embauche.getFullYear();
  let months = now.getMonth() - embauche.getMonth();
  
  if (months < 0) {
    years--;
    months += 12;
  }
  
  if (years === 0) {
    return `${months} mois`;
  } else if (months === 0) {
    return `${years} an(s)`;
  } else {
    return `${years} an(s) ${months} mois`;
  }
};

const Employes = () => {
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openEndContractDialog, setOpenEndContractDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  
  // Filter states
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    poste: '',
    est_actif: 'actif'
  });

  // Form states
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    adresse: '',
    poste: 'autre',
    salaire: '',
    date_embauche: new Date().toISOString().split('T')[0],
    notes: '',
    photo: null
  });

  const [endContractData, setEndContractData] = useState({
    date_depart: new Date().toISOString().split('T')[0],
    raison: ''
  });

  // Poste options
  const POSTE_CHOICES = [
    { value: 'cuisinier', label: 'Cuisinier' },
    { value: 'serveur', label: 'Serveur' },
    { value: 'barman', label: 'Barman' },
    { value: 'femme_chambre', label: 'Femme de chambre' },
    { value: 'autre', label: 'Autre' }
  ];

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load employes
      const response = await employeService.getAll();
      console.log('API Response for employes:', response);
      
      // Gérer différents formats de réponse
      let employeData = [];
      if (Array.isArray(response)) {
        employeData = response;
      } else if (response && Array.isArray(response.data)) {
        employeData = response.data;
      } else if (response && response.results && Array.isArray(response.results)) {
        employeData = response.results;
      } else if (response && typeof response === 'object') {
        employeData = [response];
      }
      
      console.log('Processed employes:', employeData);
      setEmployes(employeData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // S'assurer que employes est toujours un tableau
  const safeEmployes = Array.isArray(employes) ? employes : [];

  // Filter employes
  const filteredEmployes = safeEmployes.filter(employe => {
    if (!employe) return false;
    
    const matchesSearch = searchTerm === '' || 
      (employe.nom && employe.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employe.prenom && employe.prenom.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employe.telephone && employe.telephone.includes(searchTerm)) ||
      (employe.email && employe.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesPoste = !filters.poste || employe.poste === filters.poste;
    
    // Filtrer par statut actif
    let matchesStatut = true;
    if (filters.est_actif === 'actif') {
      matchesStatut = employe.est_actif === true;
    } else if (filters.est_actif === 'inactif') {
      matchesStatut = employe.est_actif === false;
    }
    
    return matchesSearch && matchesPoste && matchesStatut;
  });

  // Group employes by status
  const employesActifs = filteredEmployes.filter(e => e && e.est_actif === true);
  const employesInactifs = filteredEmployes.filter(e => e && e.est_actif === false);

  // Group by poste for statistics
  const getCountByPoste = (poste) => {
    return employesActifs.filter(e => e.poste === poste).length;
  };

  // Handler functions
  const handleOpenDialog = (employe = null) => {
    if (employe) {
      setSelectedEmploye(employe);
      setFormData({
        nom: employe.nom || '',
        prenom: employe.prenom || '',
        telephone: employe.telephone || '',
        email: employe.email || '',
        adresse: employe.adresse || '',
        poste: employe.poste || 'autre',
        salaire: employe.salaire || '',
        date_embauche: employe.date_embauche ? employe.date_embauche.split('T')[0] : new Date().toISOString().split('T')[0],
        notes: employe.notes || '',
        photo: null
      });
    } else {
      setSelectedEmploye(null);
      setFormData({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        adresse: '',
        poste: 'autre',
        salaire: '',
        date_embauche: new Date().toISOString().split('T')[0],
        notes: '',
        photo: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmploye(null);
    setFormData({
      nom: '',
      prenom: '',
      telephone: '',
      email: '',
      adresse: '',
      poste: 'autre',
      salaire: '',
      date_embauche: new Date().toISOString().split('T')[0],
      notes: '',
      photo: null
    });
  };

  const handleOpenEndContractDialog = (employe) => {
    setSelectedEmploye(employe);
    setEndContractData({
      date_depart: new Date().toISOString().split('T')[0],
      raison: ''
    });
    setOpenEndContractDialog(true);
  };

  const handleCloseEndContractDialog = () => {
    setOpenEndContractDialog(false);
    setSelectedEmploye(null);
    setEndContractData({
      date_depart: new Date().toISOString().split('T')[0],
      raison: ''
    });
  };

  const handleOpenDetailDialog = (employe) => {
    setSelectedEmploye(employe);
    setOpenDetailDialog(true);
  };

  const handleCloseDetailDialog = () => {
    setOpenDetailDialog(false);
    setSelectedEmploye(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEndContractInputChange = (e) => {
    const { name, value } = e.target;
    setEndContractData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        photo: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs au FormData
      Object.keys(formData).forEach(key => {
        if (key === 'photo' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      if (selectedEmploye) {
        // Update existing employe
        await employeService.update(selectedEmploye.id, formDataToSend);
        setSuccessMessage('Employé modifié avec succès');
      } else {
        // Create new employe
        await employeService.create(formDataToSend);
        setSuccessMessage('Employé créé avec succès');
      }
      
      await loadData();
      handleCloseDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error saving employe:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleEndContract = async (e) => {
    e.preventDefault();
    if (!selectedEmploye) return;
    
    try {
      setLoading(true);
      setError(null);
      
      await employeService.endContract(selectedEmploye.id, endContractData.date_depart);
      setSuccessMessage('Contrat terminé avec succès');
      
      await loadData();
      handleCloseEndContractDialog();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error ending contract:', err);
      setError(err.response?.data?.message || 'Erreur lors de la fin du contrat');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ? Cette action est irréversible.')) {
      try {
        setLoading(true);
        setError(null);
        
        await employeService.delete(id);
        setSuccessMessage('Employé supprimé avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReactivate = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      await employeService.update(id, { 
        est_actif: true,
        date_depart: null
      });
      setSuccessMessage('Employé réactivé avec succès');
      
      await loadData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError('Erreur lors de la réactivation');
    } finally {
      setLoading(false);
    }
  };

  const getPosteLabel = (poste) => {
    const posteObj = POSTE_CHOICES.find(p => p.value === poste);
    return posteObj ? posteObj.label : poste;
  };

  const getPhotoUrl = (employe) => {
    if (employe.photo) {
      if (typeof employe.photo === 'string') {
        // Si c'est une URL complète
        if (employe.photo.startsWith('http')) {
          return employe.photo;
        }
        // Si c'est un chemin relatif
        return `${process.env.REACT_APP_API_URL}${employe.photo}`;
      }
    }
    return null;
  };

  // Render employe card
  const renderEmployeCard = (employe) => {
    if (!employe) return null;
    
    const photoUrl = getPhotoUrl(employe);
    const anciennete = calculateAnciennete(employe.date_embauche);
    
    return (
      <Card key={employe.id || Math.random()} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="start" gap={2}>
            {/* Photo */}
            <Box>
              {photoUrl ? (
                <Avatar
                  src={photoUrl}
                  sx={{ width: 80, height: 80 }}
                  alt={`${employe.prenom} ${employe.nom}`}
                />
              ) : (
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
              )}
            </Box>
            
            {/* Informations */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {employe.prenom} {employe.nom}
                {!employe.est_actif && (
                  <Chip
                    label="Inactif"
                    color="error"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              
              <Stack spacing={1}>
                <Box display="flex" alignItems="center">
                  <WorkIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {getPosteLabel(employe.poste)}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center">
                  <AttachMoneyIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(employe.salaire)}/mois
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Embauché le {formatDate(employe.date_embauche)} ({anciennete})
                  </Typography>
                </Box>
                
                {employe.telephone && (
                  <Box display="flex" alignItems="center">
                    <PhoneIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{employe.telephone}</Typography>
                  </Box>
                )}
                
                {employe.email && (
                  <Box display="flex" alignItems="center">
                    <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{employe.email}</Typography>
                  </Box>
                )}
                
                {employe.date_depart && !employe.est_actif && (
                  <Box display="flex" alignItems="center">
                    <CalendarTodayIcon sx={{ mr: 1, fontSize: 16, color: 'error.main' }} />
                    <Typography variant="body2" color="error">
                      Départ le {formatDate(employe.date_depart)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
          
          {employe.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              <InfoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {employe.notes}
            </Typography>
          )}
        </CardContent>
        
        <CardActions>
          <Button
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => handleOpenDetailDialog(employe)}
          >
            Détails
          </Button>
          
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleOpenDialog(employe)}
          >
            Modifier
          </Button>
          
          {employe.est_actif ? (
            <Button
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleOpenEndContractDialog(employe)}
            >
              Terminer contrat
            </Button>
          ) : (
            <Button
              size="small"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleReactivate(employe.id)}
            >
              Réactiver
            </Button>
          )}
          
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete(employe.id)}
          >
            Supprimer
          </Button>
        </CardActions>
      </Card>
    );
  };

  // Loading state
  if (loading && safeEmployes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestion des Employés
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Nouvel Employé
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
            <Typography color="text.secondary" variant="body2">Employés actifs</Typography>
            <Typography variant="h4" color="success.dark">
              {employesActifs.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography color="text.secondary" variant="body2">Employés inactifs</Typography>
            <Typography variant="h4" color="error.dark">
              {employesInactifs.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
            <Typography color="text.secondary" variant="body2">Cuisiniers</Typography>
            <Typography variant="h5" color="warning.dark">
              {getCountByPoste('cuisinier')}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
            <Typography color="text.secondary" variant="body2">Serveurs</Typography>
            <Typography variant="h5" color="info.dark">
              {getCountByPoste('serveur')}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
            <Typography color="text.secondary" variant="body2">Femmes de chambre</Typography>
            <Typography variant="h5" color="primary.dark">
              {getCountByPoste('femme_chambre')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par nom, téléphone, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Poste</InputLabel>
              <Select
                value={filters.poste}
                onChange={(e) => setFilters({ ...filters, poste: e.target.value })}
                label="Poste"
                disabled={loading}
              >
                <MenuItem value="">Tous les postes</MenuItem>
                {POSTE_CHOICES.map((poste) => (
                  <MenuItem key={poste.value} value={poste.value}>
                    {poste.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.est_actif}
                onChange={(e) => setFilters({ ...filters, est_actif: e.target.value })}
                label="Statut"
                disabled={loading}
              >
                <MenuItem value="tous">Tous</MenuItem>
                <MenuItem value="actif">Actifs seulement</MenuItem>
                <MenuItem value="inactif">Inactifs seulement</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilters({ poste: '', est_actif: 'actif' })}
                disabled={loading}
              >
                Réinitialiser
              </Button>
              <Tooltip title="Actualiser">
                <IconButton onClick={loadData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge badgeContent={employesActifs.length} color="success" showZero>
                Employés actifs
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={employesInactifs.length} color="error" showZero>
                Anciens employés
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={filteredEmployes.length} color="primary" showZero>
                Tous les employés
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Employes List */}
      <Box>
        {currentTab === 0 && (
          <>
            {employesActifs.length === 0 ? (
              <Alert severity="info">Aucun employé actif</Alert>
            ) : (
              employesActifs.map(renderEmployeCard)
            )}
          </>
        )}
        
        {currentTab === 1 && (
          <>
            {employesInactifs.length === 0 ? (
              <Alert severity="info">Aucun ancien employé</Alert>
            ) : (
              employesInactifs.map(renderEmployeCard)
            )}
          </>
        )}
        
        {currentTab === 2 && (
          <>
            {filteredEmployes.length === 0 ? (
              <Alert severity="info">Aucun employé trouvé</Alert>
            ) : (
              filteredEmployes.map(renderEmployeCard)
            )}
          </>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedEmploye ? 'Modifier l\'employé' : 'Nouvel employé'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Photo upload */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  {formData.photo || (selectedEmploye && getPhotoUrl(selectedEmploye)) ? (
                    <Avatar
                      src={formData.photo ? URL.createObjectURL(formData.photo) : getPhotoUrl(selectedEmploye)}
                      sx={{ width: 100, height: 100 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}>
                      <PersonIcon fontSize="large" />
                    </Avatar>
                  )}
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        disabled={loading}
                      >
                        {formData.photo ? 'Changer la photo' : 'Télécharger une photo'}
                      </Button>
                    </label>
                    {formData.photo && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {formData.photo.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom"
                  label="Nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prenom"
                  label="Prénom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="telephone"
                  label="Téléphone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="adresse"
                  label="Adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Poste</InputLabel>
                  <Select
                    name="poste"
                    value={formData.poste}
                    onChange={handleInputChange}
                    label="Poste"
                    disabled={loading}
                  >
                    {POSTE_CHOICES.map((poste) => (
                      <MenuItem key={poste.value} value={poste.value}>
                        {poste.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="salaire"
                  label="Salaire mensuel (HTG)"
                  type="number"
                  value={formData.salaire}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                    inputProps: { min: 0, step: 1000 }
                  }}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date_embauche"
                  label="Date d'embauche"
                  type="date"
                  value={formData.date_embauche}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes (diplômes, formations, remarques)"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (selectedEmploye ? 'Modifier' : 'Créer')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* End Contract Dialog */}
      <Dialog open={openEndContractDialog} onClose={handleCloseEndContractDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleEndContract}>
          <DialogTitle>
            Terminer le contrat - {selectedEmploye?.prenom} {selectedEmploye?.nom}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Cette action marquera l'employé comme inactif.
                  Le salaire ne sera plus pris en compte dans les dépenses.
                </Alert>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date_depart"
                  label="Date de départ"
                  type="date"
                  value={endContractData.date_depart}
                  onChange={handleEndContractInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="raison"
                  label="Raison du départ"
                  value={endContractData.raison}
                  onChange={handleEndContractInputChange}
                  multiline
                  rows={3}
                  placeholder="Démission, fin de contrat, licenciement, etc."
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEndContractDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" color="error" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Terminer le contrat'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={openDetailDialog} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        {selectedEmploye && (
          <>
            <DialogTitle>
              Fiche employé - {selectedEmploye.prenom} {selectedEmploye.nom}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Photo */}
                <Grid item xs={12} md={4}>
                  <Box display="flex" justifyContent="center">
                    {getPhotoUrl(selectedEmploye) ? (
                      <Avatar
                        src={getPhotoUrl(selectedEmploye)}
                        sx={{ width: 200, height: 200 }}
                        alt={`${selectedEmploye.prenom} ${selectedEmploye.nom}`}
                      />
                    ) : (
                      <Avatar sx={{ width: 200, height: 200, bgcolor: 'primary.main' }}>
                        <PersonIcon fontSize="large" />
                      </Avatar>
                    )}
                  </Box>
                </Grid>
                
                {/* Informations */}
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {selectedEmploye.prenom} {selectedEmploye.nom}
                        <Chip
                          label={selectedEmploye.est_actif ? 'Actif' : 'Inactif'}
                          color={selectedEmploye.est_actif ? 'success' : 'error'}
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      </Typography>
                      <Chip
                        label={getPosteLabel(selectedEmploye.poste)}
                        color="primary"
                        sx={{ mb: 2 }}
                      />
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Informations personnelles
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <PhoneIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Téléphone" 
                            secondary={selectedEmploye.telephone || 'Non renseigné'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <EmailIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Email" 
                            secondary={selectedEmploye.email || 'Non renseigné'}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <HomeIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Adresse" 
                            secondary={selectedEmploye.adresse || 'Non renseigné'}
                          />
                        </ListItem>
                      </List>
                    </Box>
                    
                    <Divider />
                    
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Informations professionnelles
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemAvatar>
                            <AttachMoneyIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Salaire mensuel" 
                            secondary={formatCurrency(selectedEmploye.salaire)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <CalendarTodayIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Date d'embauche" 
                            secondary={formatDate(selectedEmploye.date_embauche)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemAvatar>
                            <CalendarTodayIcon color="action" />
                          </ListItemAvatar>
                          <ListItemText 
                            primary="Ancienneté" 
                            secondary={calculateAnciennete(selectedEmploye.date_embauche)}
                          />
                        </ListItem>
                        {selectedEmploye.date_depart && (
                          <ListItem>
                            <ListItemAvatar>
                              <CalendarTodayIcon color="error" />
                            </ListItemAvatar>
                            <ListItemText 
                              primary="Date de départ" 
                              secondary={formatDate(selectedEmploye.date_depart)}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                    
                    {selectedEmploye.notes && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Notes
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="body2">
                              {selectedEmploye.notes}
                            </Typography>
                          </Paper>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetailDialog}>
                Fermer
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
              >
                Imprimer
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  handleCloseDetailDialog();
                  handleOpenDialog(selectedEmploye);
                }}
              >
                Modifier
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Employes;